/**
 * What an appearance paints: the CSS custom properties a Studio surface reads.
 *
 * The page is black and its words are white in every colour; the colour is the accent (the survey
 * tile, the arrow circle, the progress bar, a chosen answer) and the 3D object's hue. So a choice
 * resolves to four things — the accent's fill, its solid, the ink written ON the fill, and the
 * filter that turns the orange object into that hue — plus the two typefaces.
 *
 * ── The variables live on a CONTAINER, never on `:root` ──────────────────────────────────────
 * The same landing is drawn inside the trainer's builder as a live preview. Written on the
 * document it would repaint the trainer's own app; written on the preview's box it cannot reach
 * anything outside it. Every Studio surface sets `themeVarsFor(…)` on its own root.
 *
 * ── Where each number comes from ─────────────────────────────────────────────────────────────
 * A brand preset is the app's own table (`shared/design-system/theme/brandPresets`), verbatim: its three gradient
 * stops and its solid, so a form in „Pulse" and the trainer's app in „Pulse" are the same red. The object rotation per preset is transcribed from the welcome mockups,
 * where each one was tuned by eye against the rendered object. A colour of their own is painted
 * FLAT and EXACTLY as typed, with the object rotated by the colour's hue; the words on it take
 * white or the near-black ink, whichever clears AA on that fill (`inkOnFill`).
 *
 * ── A preset's ink is decided by the same rule, not by the table (owner, 28 September 2026) ──
 * The app's table authors white for every preset but Lime, and on the form's welcome tile that
 * measured 2.35–3.56:1 across Flame's three stops: „11 pytań" failed even the large-text floor.
 * The form now runs every preset's gradient through `inkOnFill`, judged on its WORST stop, so Flame,
 * Lime, Ocean and Raspberry write the near-black and Pulse and Indigo keep white. The app's own
 * painter (`brandHues`) still reads the authored `onTile`; this is the form's decision only.
 *
 * ── A custom colour here deliberately differs from the app (owner decision, 27 September 2026) ─
 * The app moves a custom colour until a WHITE label clears AA (`brandHues`), so a bright yellow
 * becomes a mustard. On the trainer's own public page the owner wants the colour the trainer typed,
 * so the fill stays put and the ink moves instead. This applies to „Jak w aplikacji" with a custom
 * account colour too. It is not a second copy of the app's rule and is not to be merged back into
 * it: the app keeps its own rule, unchanged. The ink colours are the app's (`BRAND_LIGHT_INK`,
 * `BRAND_DARK_INK`) and the ratio is the shared `AA_TEXT_RATIO`.
 *
 * ── A line on black has a floor of its own ───────────────────────────────────────────────────
 * The page is black, and a dark colour of their own (a navy, a bottle green) drawn as a hairline, an
 * underline, a chosen answer's frame or the progress bar disappears into it. `line` is the solid
 * lifted until it holds 3:1 against the canvas (WCAG 1.4.11, non-text contrast); `bar` is the
 * preset's gradient where the canon draws one and that lifted line for a colour of their own. A
 * preset's solid already clears the floor and is left exactly as the handoff wrote it.
 */

import { BRAND_DARK_INK, BRAND_LIGHT_INK, type BrandChoice } from '~/shared/design-system/theme/brandHues';
import {
  brandGradientCss,
  brandPresetById,
  type BrandPresetId,
} from '~/shared/design-system/theme/brandPresets';
import { AA_TEXT_RATIO, contrastRatio, readableInk } from '~/shared/lib/contrast';
import { parseHex } from '~/shared/lib/hex';

import type { PublicFormAppearance, StudioPresetId } from './appearance';
import { OBJECT_BASE_HUE, bodyFont, bodyScale, headingFont, headingScale } from './catalog';

/**
 * The trainer's ACCOUNT brand colour — what „as in the app" paints. The same shared type
 * `entities/brand-color`'s `BrandColor` is, so one is passed as it is.
 */
export type AccountBrandColor = BrandChoice;

/** A resolved colour: `app` has been replaced by what the trainer's account says. */
export type StudioColorChoice =
  | { readonly kind: 'preset'; readonly preset: StudioPresetId }
  | { readonly kind: 'custom'; readonly hex: string };

export interface StudioPaint {
  /** A filled accent surface. */
  readonly fill: string;
  /** The one flat accent — a wash, a trail. */
  readonly solid: string;
  /** The accent as a LINE on the black page: a frame, an underline, a caret. At least 3:1. */
  readonly line: string;
  /** The progress bar: the preset's gradient, or `line` for a colour of their own. */
  readonly bar: string;
  /** The words drawn ON `fill`. */
  readonly onAccent: string;
  /** The CSS filter that turns the orange library object into this colour. */
  readonly objectFilter: string;
}

/** `--studio-canvas` in `tokens/studio.css`: the page every Studio line is drawn on. */
const STUDIO_CANVAS = '#000000';

/** WCAG 1.4.11: a line or a control's boundary against what surrounds it. */
const NON_TEXT_RATIO = 3;

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/** The builder's „Bez koloru": a near-white accent and a greyscale object. */
const MONO_TONE = '#f5f5f6';
const GREYSCALE_OBJECT = 'grayscale(1) brightness(1.15)';

/** Degrees each preset turns the object, tuned per preset in the welcome mockups. */
const PRESET_OBJECT_ROTATION: Readonly<Record<BrandPresetId, number>> = {
  flame: 0,
  pulse: -23,
  indigo: 217,
  lime: 66,
  ocean: 170,
  raspberry: -38,
};

/** Below this HSV saturation a colour of their own has no hue worth rotating to. */
const GREY_SATURATION = 0.12;

export function resolveStudioColor(
  color: PublicFormAppearance['color'],
  brand: AccountBrandColor | null,
): StudioColorChoice {
  // A colour of their own that is not a whole `#rrggbb` yet — empty, or half typed — reads as its
  // preset: what the builder shows while the field is being filled, and never a NaN colour.
  if (color.mode === 'custom' && color.hex !== null && HEX_PATTERN.test(color.hex)) {
    return { kind: 'custom', hex: color.hex.toLowerCase() };
  }
  if (color.mode === 'app') {
    if (brand === null) return { kind: 'preset', preset: 'flame' };
    if (brand.kind === 'custom') {
      return HEX_PATTERN.test(brand.hex) ? { kind: 'custom', hex: brand.hex.toLowerCase() } : { kind: 'preset', preset: 'flame' };
    }
    return { kind: 'preset', preset: brand.preset };
  }
  return { kind: 'preset', preset: color.preset };
}

export function studioPaint(choice: StudioColorChoice): StudioPaint {
  if (choice.kind === 'custom') return customPaint(choice.hex);
  if (choice.preset === 'mono') {
    return {
      fill: MONO_TONE,
      solid: MONO_TONE,
      line: MONO_TONE,
      bar: MONO_TONE,
      onAccent: BRAND_DARK_INK,
      objectFilter: GREYSCALE_OBJECT,
    };
  }
  const preset = brandPresetById(choice.preset) ?? brandPresetById('flame');
  if (preset === undefined) throw new Error('the brand preset table has no flame');
  const fill = brandGradientCss(preset.stops, preset.locations, '135deg');
  return {
    fill,
    solid: preset.solid,
    line: lineOnCanvas(preset.solid),
    bar: fill,
    onAccent: inkOnFill(preset.stops),
    objectFilter: `hue-rotate(${String(PRESET_OBJECT_ROTATION[preset.id])}deg)`,
  };
}

/** A colour of their own: the typed colour, flat, with the ink that reads on it — see the header. */
function customPaint(hex: string): StudioPaint {
  const { hue, saturation } = hsvOf(hex);
  const line = lineOnCanvas(hex);
  return {
    fill: hex,
    solid: hex,
    line,
    bar: line,
    onAccent: inkOnFill([hex]),
    objectFilter:
      saturation < GREY_SATURATION
        ? GREYSCALE_OBJECT
        : `hue-rotate(${String(Math.round(hue - OBJECT_BASE_HUE))}deg)`,
  };
}

/**
 * White when it clears AA on every colour of the fill, else the near-black when that does; when
 * neither does (a mid-tone, or a gradient running light to dark), whichever reads better on its
 * worst colour. White first: it is the label the app writes on most fills. A flat colour is a fill
 * of one colour.
 */
function inkOnFill(colors: readonly string[]): string {
  const worst = (ink: string) => Math.min(...colors.map((color) => contrastRatio(ink, color)));
  const light = worst(BRAND_LIGHT_INK);
  if (light >= AA_TEXT_RATIO) return BRAND_LIGHT_INK;
  const dark = worst(BRAND_DARK_INK);
  return dark >= AA_TEXT_RATIO || dark > light ? BRAND_DARK_INK : BRAND_LIGHT_INK;
}

/** The solid lifted, only as far as it must, to hold a line's floor on the black page. */
function lineOnCanvas(solid: string): string {
  return readableInk(solid, [STUDIO_CANVAS], NON_TEXT_RATIO, 'lighten');
}

/** The custom properties one appearance paints, keyed by name. Set them on the surface's own root. */
export type StudioThemeVars = Readonly<Record<`--studio-${string}`, string>>;

export function themeVarsFor(
  appearance: PublicFormAppearance,
  brand: AccountBrandColor | null,
): StudioThemeVars {
  const paint = studioPaint(resolveStudioColor(appearance.color, brand));
  const heading = headingFont(appearance.fonts.heading);
  const body = bodyFont(appearance.fonts.body);
  return {
    '--studio-accent-fill': paint.fill,
    '--studio-accent': paint.line,
    '--studio-accent-bar': paint.bar,
    '--studio-on-accent': paint.onAccent,
    '--studio-on-accent-line': withAlpha(paint.onAccent, 0.4),
    '--studio-accent-wash': withAlpha(paint.solid, 0.14),
    '--studio-accent-trail': withAlpha(paint.solid, 0.2),
    '--studio-accent-trail-line': withAlpha(paint.solid, 0.38),
    '--studio-object-filter': paint.objectFilter,
    '--studio-font-head': `'${heading.family}', 'Plus Jakarta Sans', system-ui, sans-serif`,
    '--studio-font-body': `'${body.family}', 'Plus Jakarta Sans', system-ui, sans-serif`,
    '--studio-head-weight': String(heading.weight),
    '--studio-head-track': heading.tracking,
    '--studio-head-leading': String(heading.leading),
    // Every Studio font size is multiplied by its family's factor; `m` is exactly 1.
    '--studio-head-scale': String(headingScale(appearance.fonts.headingSize)),
    '--studio-body-scale': String(bodyScale(appearance.fonts.bodySize)),
  };
}

/* ── Colour arithmetic ──────────────────────────────────────────────────────────────────────── */

function withAlpha(color: string, alpha: number): string {
  const { r, g, b } = parseHex(color);
  return `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(alpha)})`;
}

/** HSV hue in degrees (0–360) and saturation (0–1) — the space the object's rotation is tuned in. */
function hsvOf(color: string): { readonly hue: number; readonly saturation: number } {
  const { r, g, b } = parseHex(color);
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  if (d === 0) return { hue: 0, saturation: 0 };
  const sextant = max === rn ? ((gn - bn) / d) % 6 : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4;
  const hue = sextant * 60;
  return { hue: hue < 0 ? hue + 360 : hue, saturation: d / max };
}
